import type { Promisable } from 'type-fest'
import type { ISignUpData } from '@/contexts/AppUIModalContext/hooks/ui-modal'

import { useAppUIModal } from '@/contexts/AppUIModalContext'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { useSiteSpecificContext } from '@/contexts/SiteSpecificContext'
import { ProductsSubAppName } from '@/contexts/SiteSpecificContext/types'

type TValidResult = boolean

interface IValidOptions {
  showModal?: boolean
  signUpParams?: ISignUpData
}

export function useValidInfo() {
  const { isAnonymousUser, isSignIn } = useSessionInfo()
  const { plan, usage } = useSiteSpecificContext()
  const uiModal = useAppUIModal()

  return {
    isAnonymousUser,
    isSignIn,
    uiModal,
    usage,
    plan,
  }
}

type TValidHookFunc = (
  info: ReturnType<typeof useValidInfo>,
  options: IValidOptions & { isFunc?: boolean },
) => TValidResult

export enum EFeatureName {
  Generate = 'Generate', // 能否使用生成功能
}

const validGenerate: TValidHookFunc = (info, options) => {
  const { isAnonymousUser, isSignIn, uiModal, usage, plan } = info

  const { showModal, signUpParams } = options
  if (!isSignIn) {
    uiModal.signUp.show({ ...signUpParams })
    return false
  }
  const { creditsUseCount, creditsTotalCount } = usage.countInfo

  /* 非 unlimited 用户直接显示升级，unlimited用户显示联系我们 */
  if (creditsUseCount >= creditsTotalCount) {
    if (plan.creditsPlanIsUnlimitedAndActive) {
      uiModal.upgradeModal.show({
        action: 'upgrade-by-contact-unlimited-overage',
        appName: ProductsSubAppName.App1,
      })
    } else {
      uiModal.upgradePlan.show({
        reason: `You have run out of credits. To continue enjoying Tad's features, please upgrade.`,
      })
    }
    return false
  }

  return isSignIn
}

const featureValidFuncDir: Record<EFeatureName, TValidHookFunc> = {
  [EFeatureName.Generate]: validGenerate,
}

export const useFeatureGuard = (
  featureName: EFeatureName,
  options?: IValidOptions,
) => {
  const info = useValidInfo()

  const guard = useCallback<
    <T extends (...args: any[]) => Promisable<void>>(
      callback: T,
    ) => (...args: Parameters<T>) => Promisable<void>
  >(
    (callback) => {
      return async (...args) => {
        const canDo = featureValidFuncDir[featureName](info, {
          showModal: true,
          ...options,
          isFunc: true,
        })

        if (!canDo) {
          return
        }
        await callback(...args)
      }
    },
    [featureName, info, options],
  )

  return guard
}
